import { useEffect, useState } from 'react'
import { getTenantDetailsApi } from '@services/auth/auth.api'
import {
  getSignedUser,
  getTenantDetails,
  getTenantIdFromHash,
  isValidSession,
  setTenantDetails,
  type TenantDetails,
} from '@services/session'
import type { AdminBrandingConfig } from '../types/admin-config'

export interface TenantBrandingState {
  branding: AdminBrandingConfig | null
  loading: boolean
}

function resolveTenantId(): string {
  const sessionTenantId = getTenantDetails()?.tenantId
  if (sessionTenantId) return sessionTenantId

  const signedTenantId = getSignedUser().tenantId
  if (typeof signedTenantId === 'string' && signedTenantId) {
    return signedTenantId
  }

  return getTenantIdFromHash(window.location.hash)
}

function mapTenantBranding(tenant: TenantDetails | null): AdminBrandingConfig | null {
  if (!tenant) return null
  return {
    logo: tenant.logo,
    additionalLogos: tenant.additionalLogos,
    logoBackgroundColor: tenant.logoBackgroundColor,
    additionalLogoBackgroundColor: tenant.additionalLogoBackgroundColor,
    logoAltText:
      tenant.companyName ??
      tenant.accountName ??
      tenant.tenantExternalName ??
      'Tenant logo',
    logoMaxHeightPx: 28,
    selectedHeaderTheme: 'system',
  }
}

export function useTenantBranding(): TenantBrandingState {
  const [branding, setBranding] = useState<AdminBrandingConfig | null>(() =>
    mapTenantBranding(getTenantDetails()),
  )
  const [loading, setLoading] = useState(() => {
    const cached = getTenantDetails()
    return isValidSession() && Boolean(resolveTenantId()) && !cached?.logo
  })

  useEffect(() => {
    let cancelled = false

    async function refreshBranding() {
      const tenantId = resolveTenantId()
      if (!isValidSession() || !tenantId) {
        setBranding(mapTenantBranding(getTenantDetails()))
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const tenant = await getTenantDetailsApi(tenantId, {
          skipAuthRedirect: true,
        })
        if (cancelled) return
        setTenantDetails(tenant)
        setBranding(mapTenantBranding(tenant))
      } catch {
        if (cancelled) return
        setBranding(mapTenantBranding(getTenantDetails()))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void refreshBranding()

    function handleStorage() {
      setBranding(mapTenantBranding(getTenantDetails()))
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      cancelled = true
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return { branding, loading }
}
